import { Component, EventEmitter, OnInit } from '@angular/core';
import { TemplateService } from '../template.service';
import { Template } from '../template';
import { NotificationService } from '../../shared/notification.service';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector   : 'app-template-list',
  templateUrl: 'list.component.html',
  styleUrls  : [],
})
export class TemplateListComponent implements OnInit {
  public template: Template;

  /**
   * A list of all the templates currently loaded.
   *
   * @type {Template[]}
   */
  public templates: Template[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of templates.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of templates that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of templates that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a template has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public templateDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {TemplateService} templateService
   * @param {NotificationService} notificationService
   */
  constructor(
    private templateService: TemplateService,
    private notificationService: NotificationService,
    private authService: AuthService,
  )
  {
    this.templateService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.templateService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.templateService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.templateService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the template list.
   */
  public ngOnInit(): void {
    this.loadTemplates(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * template list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadTemplates(pageNumber);
  }

  /**
   * Loads a list of templates using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadTemplates(page?: number, limit?: number): void {
    const listTemplates = this.templateService
      .listTemplates(page, limit)
      .subscribe((templates: Template[]) => {
        this.templates = templates;
        listTemplates.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate template ID
   * to delete the template.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.templateService.deleteTemplate(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Template Deleted`,
          `Template with ID ${id} was deleted.`,
          5,
        );
        this.templateDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Template Not Deleted`,
          err.error.message,
          10,
        );
        this.templateDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
